import React from 'react'
import { useTranslation } from 'react-i18next'
import { IconButton, Stack, Tooltip } from '@mui/material'
import { Link } from 'react-router-dom'
import { Check, AddLink } from '@mui/icons-material'

import { formatDateYYYYMMDD, formatMoney, formatNumber } from '../../../../packages/utils/numberFormatters'
import { simplifyFraction } from '../../../../pages/issues/utils'
import { IssueTypeText } from '../../../../components/entities/IssueType'
import { ExtendedIssue } from './FLIssuesGrid'

type TableRowComponent = React.FC<{
    issue: ExtendedIssue
    onConnect: (issue: ExtendedIssue) => Promise<void>
}>

export const TableRow: TableRowComponent = ({ issue, onConnect }) => {
    const { i18n } = useTranslation()
    const locale = i18n.language

    const { sharesRequired, rightsReceived, rightsRequired, unitsReceived, unitSubTotal, matchedTo } = issue

    let unitSubPrice = issue.unitSubPrice
    const sharesInUnit = issue.sharesInUnit !== null ? issue.sharesInUnit : 1

    if (!unitSubPrice && issue.ipoIntervalLow && issue.ipoIntervalHigh) {
        const ipoIntervalLow = parseFloat(issue.ipoIntervalLow)
        const ipoIntervalHigh = parseFloat(issue.ipoIntervalHigh)

        unitSubPrice = (ipoIntervalLow + ipoIntervalHigh) / 2
    }

    const { valueOne: newShares, valueTwo: existingShares } = simplifyFraction(
        sharesInUnit * unitsReceived * rightsReceived,
        sharesRequired * rightsRequired,
    )
    const isMatchedTo = matchedTo !== null

    const issueDate = issue.exRightsDate
        ? formatDateYYYYMMDD(issue.exRightsDate)
        : issue.subEndDate
          ? formatDateYYYYMMDD(issue.subEndDate)
          : formatDateYYYYMMDD(issue.publicationDate)

    return (
        <tr
            style={{
                height: '56px',
            }}
        >
            <td>
                <Stack direction='row' spacing={2} alignItems={'center'}>
                    <Tooltip title='Koppla FL-emission till MC-emission'>
                        <IconButton
                            sx={{
                                width: 30, // Adjust the width for your desired size
                                height: 30, // Adjust the height for your desired size
                                borderRadius: '50%', // Makes the button round
                                backgroundColor: isMatchedTo ? 'success.main' : 'primary.main', // Optional: Set a background color for better visibility
                                border: '2px solid', // Adds a border
                                borderColor: isMatchedTo ? 'success.main' : 'primary.main', // Set border color with main variant
                                color: 'white', // Set icon color for better contrast
                                '&:hover': {
                                    backgroundColor: isMatchedTo ? 'success.dark' : 'primary.dark', // Darker color on hover
                                    borderColor: isMatchedTo ? 'success.dark' : 'primary.dark', // Darker border color on hover
                                },
                            }}
                            onClick={() => {
                                void onConnect(issue)
                            }}
                        >
                            {isMatchedTo && <Check sx={{ fontSize: 22 }} />}
                            {!isMatchedTo && <AddLink sx={{ fontSize: 22 }} />}
                        </IconButton>
                    </Tooltip>
                    <Stack>
                        <Link to={`/admin/issues/${issue.id}`} target='_blank'>
                            {issue.shareName} - {issueDate}
                        </Link>
                        <i>
                            {issue.type === 'Non-cash issue' ? (
                                'AE - ' + issue.noncashConsideration
                            ) : (
                                <IssueTypeText issueType={issue.type} />
                            )}
                        </i>
                    </Stack>
                </Stack>
            </td>
            <td>
                {unitSubPrice
                    ? formatMoney(unitSubPrice / sharesInUnit, {
                          locale,
                          currencySymbol: issue.currency,
                          minDecimals: 2,
                          maxDecimals: 5,
                      })
                    : 'Ej fastställt ännu'}
            </td>
            <td>{newShares > 0 && existingShares > 0 ? `${newShares}:${existingShares}` : '-'}</td>
            <td>{formatNumber(unitSubTotal * sharesInUnit, { decimals: 0, locale })}</td>
        </tr>
    )
}
